import { cx } from 'class-variance-authority';
import type React from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { forwardRef, useId } from 'react';
import Hint from '../hint';
import Label from '../label';

export type TextAreaProps = {
  className?: string;
  hasError?: boolean;
  hint?: string;
  label?: string;
  onChangeText?: (nextValue: string) => void;
  size?: 'default' | 'compact';
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      hasError = false,
      hint,
      id: baseID,
      label,
      onChange,
      onChangeText,
      placeholder,
      className,
      size = 'default',
      ...props
    }: TextAreaProps,
    ref,
  ) => {
    const internalId = useId();
    const id = baseID || internalId;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(event);
      }
      if (onChangeText) {
        onChangeText(event.currentTarget.value);
      }
    };

    return (
      <div>
        {label && (
          <Label htmlFor={id} size={size}>
            {label}
          </Label>
        )}
        <textarea
          className={cx('fp-textarea', className)}
          data-has-error={hasError}
          data-size={size}
          id={id}
          onChange={handleChange}
          placeholder={placeholder}
          ref={ref}
          {...props}
        />
        {hint && <Hint hasError={hasError}>{hint}</Hint>}
      </div>
    );
  },
);

export default TextArea;
